<script setup>
import { ref, onMounted, computed } from 'vue'
import { fetchContactData } from '@/services/api'
import DynamicHero from '../components/DynamicHero.vue'

// Fallback images
import bgHero from '@/assets/img/bg-contact.png'
import bgGradient from '@/assets/img/bg-gradient-smilepicture.png'

// Default contact data (fallback)
const defaultContactData = {
    address: 'Jl. Surapati Core Jl. Anggrek Boulevard Blok AB No.3, Pasirlayung, Kec. Cibeunying Kidul, Kota Bandung, Jawa Barat 40192',
    googleMapsEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3960.912481186623!2d107.6495580738681!3d-6.901069667533525!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e68e473c913eff1%3A0x6fec59e34204c28a!2sPT%20Rancang%20Desain%20Program%20Linimasa!5e0!3m2!1sen!2sid!4v1768199145452!5m2!1sen!2sid',
    email: 'team@smilepictures.id',
    phone: '6281223909388',
    whatsapp: '6281223909388',
    youtube: null,
    facebook: null,
    instagram: null,
    linkedin: null,
    twitter: null,
    tiktok: null
}

const contactData = ref(defaultContactData)
const slides = ref([])
const isLoading = ref(true)

// Format phone number for display
const formatPhone = (phone) => {
    if (!phone) return ''
    // Add +62 prefix if not present and format nicely
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.startsWith('62')) {
        return '+62 ' + cleaned.substring(2).replace(/(\d{4})(\d{4})/, '$1-$2')
    } else if (cleaned.startsWith('0')) {
        return '+62 ' + cleaned.substring(1).replace(/(\d{4})(\d{4})/, '$1-$2')
    }
    return '+' + cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '$1 $2-$3')
}

// Create WhatsApp link
const whatsappLink = computed(() => {
    const phone = contactData.value.whatsapp || contactData.value.phone
    return phone ? `https://wa.me/${phone}` : '#'
})

// Format phone for display
const displayPhone = computed(() => {
    const phone = contactData.value.phone
    return formatPhone(phone)
})

onMounted(async () => {
    try {
        const response = await fetchContactData()
        if (response) {
            contactData.value = { ...defaultContactData, ...response.contact }
            slides.value = response.slides || []
        }
    } catch (error) {
        console.error('Failed to load contact data:', error)
        // Use default data on error
    } finally {
        isLoading.value = false
    }
})
</script>

<template>
    <!-- HERO SECTION -->
    <DynamicHero :slides="slides" :fallbackBg="bgHero" fallbackTitle="Let's Create Something Meaningful"
        fallbackDescription="Have a project, an idea, or a story to tell? We're ready to collaborate and bring it to life through powerful visuals."
        :fallbackButtonText="''" fallbackButtonLink="#" breadcrumbLabel="Contact" />

    <section class="wrapper bg-black">
        <div class="container">
            <div class="row">
                <div class="row gy-10 gx-lg-8 gx-xl-12">

                    <!-- MAPS -->
                    <div class="col-lg-8">
                        <div class="card bg-dark border-0" style="border-radius:16px; overflow:hidden;">
                            <div style="width:100%; height:520px; position:relative;">
                                <iframe title="Google Maps - SmilePictures Location"
                                    :src="contactData.googleMapsEmbedUrl"
                                    style="width:100%; height:100%; border:0; position:absolute; inset:0;"
                                    allowfullscreen loading="lazy" referrerpolicy="no-referrer-when-downgrade">
                                </iframe>
                            </div>
                        </div>
                    </div>

                    <!-- ASIDE (BG GRADIENT IMAGE) -->
                    <aside class="col-lg-4">
                        <div class="card border-0 h-100 gradient-aside text-white" :style="{
                            backgroundImage: `url(${bgGradient})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                        }">
                            <div class="card-body">

                                <!-- TITLE -->
                                <h3 class="mb-5 text-white">Contact Us Here</h3>

                                <!-- EMAIL -->
                                <div class="contact-item" v-if="contactData.email">
                                    <i class="text-white uil uil-envelope"></i>
                                    <a :href="`mailto:${contactData.email}`" style="color:#ddd; text-decoration:none;">
                                        {{ contactData.email }}
                                    </a>
                                </div>

                                <!-- PHONE / WHATSAPP -->
                                <div class="contact-item" v-if="contactData.phone || contactData.whatsapp">
                                    <i class="text-white uil uil-phone"></i>
                                    <a :href="whatsappLink" target="_blank" rel="noopener"
                                        style="color:#ddd; text-decoration:none;">
                                        {{ displayPhone }}
                                    </a>
                                </div>

                                <!-- ADDRESS -->
                                <div class="contact-item" v-if="contactData.address">
                                    <i class="text-white uil uil-map-marker"></i>
                                    <span style="color:#ddd;">
                                        {{ contactData.address }}
                                    </span>
                                </div>

                                <!-- FOLLOW -->
                                <h3 v-if="contactData.youtube || contactData.facebook || contactData.instagram || contactData.linkedin || contactData.twitter || contactData.tiktok"
                                    class="mb-5 text-white">Follow Us</h3>
                                <nav class="social-icons">

                                    <!-- YouTube -->
                                    <a v-if="contactData.youtube" :href="contactData.youtube" target="_blank"
                                        rel="noopener" class="social-link" aria-label="icon youtube">
                                        <i class="uil uil-youtube"></i>
                                    </a>

                                    <!-- Facebook -->
                                    <a v-if="contactData.facebook" :href="contactData.facebook" target="_blank"
                                        rel="noopener" class="social-link" aria-label="icon facebook">
                                        <i class="uil uil-facebook-f"></i>
                                    </a>

                                    <!-- Instagram -->
                                    <a v-if="contactData.instagram" :href="contactData.instagram" target="_blank"
                                        rel="noopener" class="social-link" aria-label="icon instagram">
                                        <i class="uil uil-instagram"></i>
                                    </a>

                                    <!-- LinkedIn -->
                                    <a v-if="contactData.linkedin" :href="contactData.linkedin" target="_blank"
                                        rel="noopener" class="social-link" aria-label="icon linkedin">
                                        <i class="uil uil-linkedin"></i>
                                    </a>

                                    <!-- Twitter / X -->
                                    <a v-if="contactData.twitter" :href="contactData.twitter" target="_blank"
                                        rel="noopener" class="social-link" aria-label="icon twitter">
                                        <i class="uil uil-twitter"></i>
                                    </a>

                                    <!-- TikTok -->
                                    <a v-if="contactData.tiktok" :href="contactData.tiktok" target="_blank"
                                        rel="noopener" class="social-link" aria-label="icon tiktok">
                                        <i class="uil uil-video"></i>
                                    </a>
                                </nav>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    </section>
</template>

<style>
/* ===== FORM DARK MODE ===== */
.form-dark {
    background-color: #1f1f1f;
    border: 1px solid #333;
    color: #fff;
}

.form-dark:focus {
    background-color: #1f1f1f;
    border-color: #ffae0b;
    color: #fff;
    box-shadow: none;
}

.form-floating>label {
    color: #aaa;
}

/* ===== ASIDE GRADIENT ===== */
.gradient-aside {
    border-radius: 16px;
    position: relative;
    overflow: hidden;
}

.gradient-aside::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 0;
}

.gradient-aside .card-body {
    position: relative;
    z-index: 1;
}

/* ===== CONTACT ITEM ===== */
.contact-item {
    display: flex;
    gap: 16px;
    margin-bottom: 32px;
}

.contact-item i {
    font-size: 28px;
    color: #ffae0b;
    line-height: 1;
}

.contact-item h5 {
    margin-bottom: 4px;
    color: #fff;
}

.contact-item p {
    margin-bottom: 0;
    color: #ddd;
}

.gradient-aside {
    border-radius: 16px;
    position: relative;
    overflow: hidden;
}

.gradient-aside::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    z-index: 0;
}

.gradient-aside>* {
    position: relative;
    z-index: 1;
}

/* ===== SOCIAL ICONS ===== */
.social-icons {
    display: flex;
    gap: 1rem;
}

.social-link i {
    font-size: 26px;
    color: #fff;
    transition: color 0.25s ease, transform 0.25s ease;
}

.social-link:hover i {
    color: #ffae0b;
    transform: translateY(-2px);
}
</style>
